// Lightweight fetch-based API wrapper (keeps axios-like surface)
// Improves bundle size by avoiding the full axios package where possible.
const API_BASE = 'https://ostdata.gprlive.com/api';
const IMAGE_BASE = 'https://ostdata.gprlive.com/';

export function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

function buildUrl(path, params = {}) {
  const base = path.startsWith('http') ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
  const url = new URL(base);
  Object.keys(params || {}).forEach((k) => {
    const v = params[k];
    if (v === undefined || v === null) return;
    if (Array.isArray(v)) v.forEach((vv) => url.searchParams.append(`${k}[]`, vv));
    else url.searchParams.set(k, String(v));
  });
  return url.toString();
}

async function request(method, path, options = {}) {
  const { params, data, headers = {}, timeout = 10000, responseType } = options;
  const url = buildUrl(path, params);
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  const authToken = getCookie('authToken') || localStorage.getItem('auth_token') || localStorage.getItem('authToken');
  const mergedHeaders = Object.assign({ Accept: 'application/json' }, headers);
  if (authToken) mergedHeaders['Authorization'] = `Bearer ${authToken}`;

  const fetchOptions = {
    method: method.toUpperCase(),
    headers: mergedHeaders,
    signal: controller.signal,
  };

  if (data !== undefined && data !== null) {
    if (data instanceof FormData) {
      delete fetchOptions.headers['Content-Type'];
      fetchOptions.body = data;
    } else if (responseType === 'blob') {
      fetchOptions.headers['Content-Type'] = 'application/json';
      fetchOptions.body = JSON.stringify(data);
    } else {
      fetchOptions.headers['Content-Type'] = 'application/json';
      fetchOptions.body = JSON.stringify(data);
    }
  }

  try {
    const res = await fetch(url, fetchOptions);
    clearTimeout(id);
    if (!res.ok) {
      // Try to parse JSON error body when available so callers can inspect validation errors
      let payload = null
      let errText = res.statusText || `HTTP error ${res.status}`
      try {
        // Try parse JSON first
        payload = await res.json()
        // Keep readable message for Error
        errText = (typeof payload === 'string') ? payload : JSON.stringify(payload)
      } catch (e) {
        // fallback to plain text
        try { errText = await res.text() } catch (e2) { /* ignore */ }
      }

      const err = new Error(errText || `HTTP error ${res.status}`)
      err.status = res.status
      // Attach parsed response-like object so existing axios-style handlers work
      err.response = { data: payload }
      throw err
    }
    let payload;
    if (responseType === 'blob') payload = await res.blob();
    else {
      try { payload = await res.json(); } catch (e) { payload = await res.text(); }
    }
    return { data: payload, status: res.status, headers: res.headers };
  } catch (err) {
    clearTimeout(id);
    if (err.name === 'AbortError') throw new Error('Request timed out');
    throw err;
  }
}

const api = {
  get: (path, opts) => request('get', path, opts),
  post: (path, data, opts = {}) => request('post', path, Object.assign({}, opts, { data })),
  put: (path, data, opts = {}) => request('put', path, Object.assign({}, opts, { data })),
  delete: (path, opts = {}) => request('delete', path, opts),
  request,
};

export { api, IMAGE_BASE, API_BASE };
export default api;

// Compatibility wrappers (commonly used helpers across the project)
export async function fetchCurrentUserGroups(params = {}) {
  const resp = await api.get('/current-user-groups', { params });
  return resp.data;
}
export async function fetchQuizzes(params = {}) {
  const resp = await api.get('/quizzes', { params });
  return resp.data;
}
export async function fetchResources(params = {}) {
  const resp = await api.get('/resources', { params });
  return resp.data;
}
export async function fetchResource(slug) {
  const resp = await api.get(`/resources/${slug}`);
  return resp.data;
}
export async function getUserProfile(username) {
  const resp = await api.get(`/getuser/${encodeURIComponent(username)}`);
  return resp.data?.success ? resp.data.data : resp.data;
}

export async function getFollows(params = {}) { const resp = await api.get('/follows', { params }); return resp.data; }
export async function getUserFollows(userId, params = {}) { const resp = await api.get(`/follows/user/${userId}`, { params }); return resp.data; }
export async function followUser(userId) { const resp = await api.post(`/follows/${userId}`); return resp.data; }
export async function unfollowUser(userId) { const resp = await api.delete(`/follows/${userId}`); return resp.data; }
export async function getPendingRequests() { const resp = await api.get('/follows/requests/pending'); return resp.data; }
export async function getSentRequests() { const resp = await api.get('/follows/requests/sent'); return resp.data; }
export async function acceptFollowRequest(requestId) { const resp = await api.post(`/follows/requests/${requestId}/accept`); return resp.data; }
export async function rejectFollowRequest(requestId) { const resp = await api.post(`/follows/requests/${requestId}/reject`); return resp.data; }
export async function cancelFollowRequest(requestId) { const resp = await api.delete(`/follows/requests/${requestId}`); return resp.data; }

// New API wrappers for /follow-requests endpoints (registered in backend api.php)
export async function getFollowRequests(params = {}) { const resp = await api.get('/follow-requests', { params }); return resp.data; }
export async function acceptFollowRequestByUser(userId) { const resp = await api.post(`/follow-requests/${userId}/accept`); return resp.data; }
export async function rejectFollowRequestByUser(userId) { const resp = await api.delete(`/follow-requests/${userId}`); return resp.data; }
export async function blockUser(userId, reason = '') { const resp = await api.post(`/follows/block/${userId}`, { reason }); return resp.data; }
export async function unblockUser(userId) { const resp = await api.delete(`/follows/block/${userId}`); return resp.data; }
export async function getBlockedUsers() { const resp = await api.get('/follows/blocked'); return resp.data; }
export async function getFollowSuggestions(limit = 10) { const resp = await api.get('/follows/suggestions', { params: { limit } }); return resp.data; }
export async function getFollowStatistics(userId = null) { const url = userId ? `/follows/statistics/${userId}` : '/follows/statistics'; const resp = await api.get(url); return resp.data; }
export async function getMutualFollowers(userId) { const resp = await api.get(`/follows/mutual/${userId}`); return resp.data; }
export async function updatePrivacySettings(settings) { const resp = await api.put('/privacy', settings); return resp.data; }
export async function checkFollowStatus(userId) { const resp = await api.get(`/follows/check/${userId}`); return resp.data; }
export async function checkFollowRequestStatus(userId) { const resp = await api.get(`/follows/requests/status/${userId}`); return resp.data; }

